import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import FontFamily from '../../../../constants/fonts';

let VictoryPie: any, VictoryContainer: any, VictoryLabel: any;
if (Platform.OS === 'web') {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer, VictoryLabel } = require('victory'));
} else {
  // @ts-ignore
  ({ VictoryPie, VictoryContainer, VictoryLabel } = require('victory-native'));
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChartDataItem {
  /** Segment label displayed in the legend */
  x: string;
  /** Raw count — VictoryPie will compute proportions from these values */
  y: number;
  /** Hex color for the segment and its legend dot */
  color: string;
}

export interface VictoryChartCardProps {
  title: string;
  data: ChartDataItem[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_HORIZONTAL_PADDING = 32; // 16px padding each side
const MAX_CHART_SIZE = 180;         // Never grow the donut beyond this
const INNER_RADIUS_RATIO = 80 / 180; // Matches SiteEvaluation.tsx thin-ring ratio

/** Slices below this fraction of the total get their count shown in the legend only */
const SMALL_SLICE_THRESHOLD = 0.05; // 5 %

/**
 * Returns '#FFFFFF' or '#2D2D2D' depending on which gives better contrast
 * against the supplied hex background color.
 */
function getContrastColor(hexColor: string): string {
  const hex = (hexColor || '#888888').replace('#', '');
  if (hex.length < 6) return '#FFFFFF';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Perceived luminance (ITU-R BT.601)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#2D2D2D' : '#FFFFFF';
}

// ─── Component ───────────────────────────────────────────────────────────────

export const VictoryChartCard: React.FC<VictoryChartCardProps> = ({
  title,
  data,
}) => {
  const [cardWidth, setCardWidth] = useState(0);

  // Responsive chart size: fill available width (minus padding), capped at MAX_CHART_SIZE
  const availableWidth = cardWidth > 0 ? cardWidth - CARD_HORIZONTAL_PADDING : MAX_CHART_SIZE;
  const chartSize = Math.min(Math.max(availableWidth, 100), MAX_CHART_SIZE);
  const innerRadius = Math.round(chartSize * INNER_RADIUS_RATIO);

  // Memoize chart data to prevent unnecessary recalculations
  const chartData = useMemo(() => {
    const isEmpty = data.length === 0;
    return isEmpty
      ? [{ x: 'No Data', y: 1, color: '#E8ECF1' }]
      : data;
  }, [data]);

  // Generate a unique key based on data content to force Victory re-render when data changes
  // This ensures the chart properly updates when filter changes affect category counts
  // Include title to ensure each chart instance is independent
  const chartDataKey = useMemo(() => {
    const dataKey = chartData
      .map((item) => `${item.x}:${item.y}`)
      .join('|');
    return `${title}-${dataKey}`;
  }, [chartData, title]);

  const isEmpty = data.length === 0;
  const totalForPercent = data.reduce((sum, d) => sum + d.y, 0);
  
  // Calculate total count for center display (show actual count, not placeholder)
  const totalCount = isEmpty ? 0 : totalForPercent;

  // Calculate responsive items per row based on card width
  const getItemsPerRow = (): number => {
    if (cardWidth <= 0) return 2;
    if (cardWidth < 250) return 2;  // Very small cards
    if (cardWidth < 350) return 3;  // Small-medium cards
    return 4;                        // Normal/large cards
  };

  const itemsPerRow = getItemsPerRow();

  return (
    <View
      style={styles.card}
      onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}
    >
      {/* ─── Card title ───────────────────────────────────────────── */}
      <Text style={styles.cardTitle}>{title}</Text>

      {/* ─── Donut chart ──────────────────────────────────────────── */}
      {cardWidth > 0 && (
        <View style={styles.chartWrapper}>
          <VictoryPie
            key={chartDataKey}
            width={chartSize}
            height={chartSize}
            data={chartData}
            innerRadius={innerRadius}
            padAngle={0}
            containerComponent={
              <VictoryContainer
                responsive={false}
                style={{ touchAction: 'auto', userSelect: 'auto' }}
              />
            }
            labels={({ datum }: any) => {
              // Show count inside slice only when it is large enough to be readable.
              // Small slices already have their count shown in the legend below.
              if (isEmpty || totalForPercent === 0) return null;
              const pct = datum.y / totalForPercent;
              return pct >= SMALL_SLICE_THRESHOLD ? String(datum.y) : null;
            }}
            labelComponent={
              <VictoryLabel
                textAnchor="middle"
                verticalAnchor="middle"
                style={{
                  fontSize: Math.max(10, Math.round(chartSize * 0.072)),
                  fontWeight: '400',
                  fontFamily: FontFamily.regular,
                  // fill is resolved per-datum inside Victory via a function
                  fill: ({ datum }: any) =>
                    getContrastColor(datum?.color || '#E8ECF1'),
                }}
              />
            }
            {...(Platform.OS !== 'web'
              ? {
                  animate: {
                    duration: 800,
                    easing: 'bounce',
                    onLoad: { duration: 500 },
                  },
                }
              : {})}
            style={{
              data: {
                fill: ({ datum }: any) => datum.color || '#E8ECF1',
                fillOpacity: 0.98,
              },
            }}
          />
          {/* ─── Center text showing total count ─────────────────────── */}
          <View style={styles.centerTextContainer}>
            {/* <Text style={styles.totalLabel}>Total</Text> */}
            <Text style={styles.totalCount}>{totalCount}</Text>
          </View>
        </View>
      )}

      {/* ─── Legend — responsive layout ─────────────────────────────────── */}
      <View style={styles.legendContainer}>
        {(() => {
          // Use chartData (filtered) instead of data (unfiltered) to match what's displayed
          const legendData = isEmpty ? [] : chartData;
          const rows: ChartDataItem[][] = [];
          for (let i = 0; i < legendData.length; i += itemsPerRow) {
            rows.push(legendData.slice(i, i + itemsPerRow));
          }
          return rows.map((rowItems, rowIndex) => (
            <View key={rowIndex} style={styles.legendRow}>
              {rowItems.map((item, index) => (
                <View key={index} style={styles.legendItem}>
                  <View style={[styles.legendDot, { backgroundColor: item.color }]} />
                  <Text 
                    style={styles.legendLabel}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {item.x} ({item.y})
                  </Text>
                </View>
              ))}
            </View>
          ));
        })()}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E0E8F5',
    padding: 16,
    alignItems: 'center',
    overflow: 'hidden', // Prevent content overflow from card
    // Shadow — matches Figma card depth
    shadowColor: '#005CBC',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  cardTitle: {
    alignSelf: 'flex-start',
    fontSize: 16,
    fontWeight: '700',
    color: '#616161',
    fontFamily: FontFamily.bold,
    marginBottom: 12,
    lineHeight: 22,
  },
  chartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden', // Prevent chart overflow
    position: 'relative', // Allow absolute positioning of center text
  },
  centerTextContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none', // Allow clicks to pass through to chart
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#616161',
    fontFamily: FontFamily.bold,
    lineHeight: 16,
    marginBottom: 2,
  },
  totalCount: {
    fontSize: 20,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    lineHeight: 24,
  },
  legendContainer: {
    marginTop: 12,
    alignSelf: 'stretch',
    gap: 6,
    overflow: 'hidden', // Prevent overflow from container
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',    // Allow wrapping if items don't fit
    justifyContent: 'center',
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    maxWidth: '45%',     // Prevent items from taking too much space
    minWidth: 80,        // Ensure minimum readable width
    flexShrink: 1,       // Allow shrinking if needed
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 6,
    flexShrink: 0,       // Never shrink the dot
  },
  legendLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
    lineHeight: 22,
    flexShrink: 1,       // Allow label text to shrink
    flexWrap: 'wrap',
  },
});

export default VictoryChartCard;
